import { makeAutoObservable, reaction, runInAction, when } from 'mobx'
import moment from 'moment'
import UserProfileService from './../../../shared/services/UserProfile.service'
import LeadVendorService from './../../../shared/services/LeadVendor.service'
import StorageService from './../../../shared/services/Storage.service'
import { LedgerFactory, LeadCreditPackageFactory, LedgerTransactionFactory } from './../../../shared/factories'
import { isWhiteLabel } from './../../../shared/utilities/whiteLabel.function'
import TransferStore from './../../ledgers/store/Transfer.store'
import LeadCreditPurchaseModal from './../../modals/LeadCreditPurchaseModal'

export const ONBOARDING_LEADS_WINDOW_DAYS = 10

const DISMISSED_KEY = 'usabg:onboarding-leads:dismissed'
const TRANSFER_FORM = 'LeadVendorTransfer'
const TRANSFER_REQUIRED = { purpose: false, lead_order_state: true }

// The ONBOARDING-COMPLETE award is posted after /onboarding/complete responds,
// so the completion modal can load before the bonus lands.
const AWARD_RETRY_MS = 1500
const AWARD_RETRIES = 8

const findTransactions = async search => {
  try {
    return (await LedgerTransactionFactory.search({ search, pagination: false })) || []
  } catch (ex) {
    return []
  }
}

class OnboardingLeadsStore {
  Ledger = null
  Packages = []
  onboardedAt = null
  hasTransferredSince = false
  isLoading = false
  isLoaded = false
  feedFailed = false
  isBuying = false
  transferFormVersion = 0
  isDismissed = false

  constructor() {
    makeAutoObservable(this)
  }

  get isEligible() {
    return !isWhiteLabel() && UserProfileService.isA(['signature-group'])
  }

  get balance() {
    const balance = parseFloat(this.Ledger?.get('balance'))
    return isNaN(balance) ? 0 : balance
  }

  get showCard() {
    if (!this.isEligible || !this.isLoaded || this.isDismissed || this.hasTransferredSince || !this.onboardedAt)
      return false
    return moment().diff(moment(this.onboardedAt), 'days', true) <= ONBOARDING_LEADS_WINDOW_DAYS
  }

  dismissKey = () => `${DISMISSED_KEY}:${UserProfileService.getUserId()}`

  load = async ({ awaitAward } = {}) => {
    this.isLoading = true
    this.isDismissed = `${StorageService.get(this.dismissKey()) || ''}` === '1'

    await Promise.all([this.fetchFeed(), TransferStore.fetchLeadVendors()])
    await this.fetchOnboardingActivity()

    if (this.Ledger && (TransferStore.form !== TRANSFER_FORM || TransferStore.Ledger?.id() !== this.Ledger.id()))
      this.armTransfer()

    runInAction(() => {
      this.isLoading = false
      this.isLoaded = true
    })

    if (awaitAward) this.awaitAward()
  }

  awaitAward = async () => {
    const hadLedger = !!this.Ledger
    for (let attempt = 0; attempt < AWARD_RETRIES && !this.onboardedAt; attempt++) {
      await new Promise(resolve => setTimeout(resolve, AWARD_RETRY_MS))
      if (!this.Ledger) await this.fetchFeed()
      else await this.reloadLedger()
      await this.fetchOnboardingActivity()
    }
    if (this.onboardedAt) await this.reloadLedger()
    if (!hadLedger && this.Ledger) this.armTransfer()
  }

  fetchFeed = async () => {
    let LeadVendors = [],
      Ledger = null,
      LeadCreditPackages = [],
      feedFailed = false
    try {
      ;({ LeadVendors, Ledger, LeadCreditPackages } = (await LeadVendorService.pages('order-leads')) || {})
    } catch (ex) {
      feedFailed = true
      console.error(`Failed to load onboarding lead packages. ${ex}`)
    }

    const vendorIds = (Array.isArray(LeadVendors) ? LeadVendors : [])
      .filter(Vendor => !Vendor.deleted_at)
      .map(Vendor => `${Vendor.id}`)

    runInAction(() => {
      this.feedFailed = feedFailed
      this.Ledger = Ledger ? LedgerFactory.create(Ledger) : null
      this.Packages = (Array.isArray(LeadCreditPackages) ? LeadCreditPackages : [])
        .filter(Pkg => `${Pkg.is_onboarding}` === '1' && !Pkg.deleted_at && vendorIds.includes(`${Pkg.lead_vendor_id}`))
        .map(Pkg => LeadCreditPackageFactory.create(Pkg))
        .sort((a, b) => `${a.get('name')}`.localeCompare(`${b.get('name')}`, 'en'))
    })
  }

  fetchOnboardingActivity = async () => {
    const ledgerId = this.Ledger?.id()
    if (!ledgerId) return

    const Award = (await findTransactions({ ledger_id: ledgerId, category: 'ONBOARDING-COMPLETE' })).shift(),
      onboardedAt = Award ? Award.get('created_at') : null,
      Transfers = onboardedAt ? await findTransactions({ ledger_id: ledgerId, type: TRANSFER_FORM }) : []

    runInAction(() => {
      this.onboardedAt = onboardedAt
      this.hasTransferredSince = Transfers.some(
        T => T.get('trans_state') !== 'CANCELED' && moment(T.get('created_at')).isSameOrAfter(moment(onboardedAt))
      )
    })
  }

  reloadLedger = async () => {
    if (!this.Ledger) return
    try {
      await this.Ledger.reload()
    } catch (ex) {
      await this.fetchFeed()
    }
  }

  armTransfer = () => {
    TransferStore.minimumRequired = null
    TransferStore.availableStates = null
    TransferStore.create(this.Ledger)
    TransferStore.form = TRANSFER_FORM
    TransferStore.transfer.doner_id = this.Ledger?.id()
    TransferStore.showErrors = false
  }

  // An outcome message describes the previous entry, so it goes once the agent edits the form.
  watchEdits = () =>
    reaction(
      () => [TransferStore.transfer.lead_vendor_id, TransferStore.transfer.amount],
      ([vendorId, amount]) => {
        if (!vendorId && !amount) return
        runInAction(() => {
          TransferStore.outcomeMsg = ''
          TransferStore.showErrorMsg = false
          TransferStore.showSuccessMsg = false
        })
      }
    )

  // MDBModal only rebuilds its Tab order when its children change, and UISelect swaps its
  // input just after mounting. Modals hosting the panel read this to re-render once the form is in place.
  transferFormMounted = () => this.transferFormVersion++

  submitTransfer = async () => {
    if (!(await TransferStore.validate({ required: TRANSFER_REQUIRED }))) return

    await TransferStore.store()

    if (TransferStore.showSuccessMsg) {
      // store() clears the form shortly after a success; re-open it for the next order.
      await when(() => !TransferStore.form)
      this.armTransfer()
      runInAction(() => (this.hasTransferredSince = true))
    }

    await this.reloadLedger()
  }

  buyBucks = async () => {
    if (this.isBuying || !this.Ledger) return
    this.isBuying = true

    try {
      await LeadCreditPurchaseModal.open({
        title: 'Add USABG BUCK$',
        user: await UserProfileService.instance(),
        ledger: this.Ledger,
      })
    } catch (ex) {}

    await this.reloadLedger()
    runInAction(() => {
      // A rejection from before the purchase no longer reflects the balance.
      if (TransferStore.showErrorMsg) {
        TransferStore.outcomeMsg = ''
        TransferStore.showErrorMsg = false
      }
      this.isBuying = false
    })
  }

  dismiss = () => {
    StorageService.set(this.dismissKey(), '1')
    this.isDismissed = true
  }

  end = () => {
    if (TransferStore.form === TRANSFER_FORM) TransferStore.end()
  }
}

export default new OnboardingLeadsStore()
